'use client';

import type { ReactNode } from 'react';
import type { TutorBundle } from '@peach/types/tutors/tutor-profile.dto.js';
import { firstName } from '@/lib/utils/displayName';
import { callWhen } from '@/lib/utils/when';
import { useClock } from '@/lib/hooks/useClock';
import dynamic from 'next/dynamic';
import { IntroVideo } from '@/components/media/IntroVideo';
import { InlineAuth } from '@/components/auth/InlineAuth';
import { BookSlotCard } from './BookSlotCard';
import { OwnerHours } from './OwnerHours';
import type { ScheduleDto } from '@peach/types/schedule/schedule.dto.js';
import { nextCallLine, ownerHoursLine, pickTimeAsk } from './intro-copy';

// Owner-only, and it drags the whole mux-uploader stack with it — visitors
// must not pay for a control only the page's owner ever renders.
const OwnerIntroVideo = dynamic(() => import('./OwnerIntroVideo').then((m) => m.OwnerIntroVideo), {
  ssr: false,
});

interface IntroCardProps {
  tutorUsername: string;
  tutorName: string;
  loggedIn: boolean;
  playbackId?: string | null;
  picture?: string | null;
  /** What to sell this viewer, rendered the quiz result's way: the calendar
      open under this card's headline, the price on the Book button that follows
      the picked time. Null when there is nothing to sell or nothing to ask —
      the page asks one thing at a time. The grid sells it too, as its first
      card: this one is calendar-first, that one the plain buy. */
  bundle?: TutorBundle | null;
  /** The viewer's soonest call with this provider, named in the headline. */
  nextCallAt?: number | null;
  /** The viewer holds an unspent pass and no call: the ask is to pick a time. */
  hasPass?: boolean;
  /** The viewer's zone, null until the browser says; the hour waits for it. */
  timezone: string | null;
  /** A bearer link to one offer: the intro sells nothing beside it and asks
      for no sign-in — the offer card does both. */
  offerOnly?: boolean;
  isOwner?: boolean;
  /** The owner's weekly hours, open where a visitor reads the calendar: the
      card IS the calendar, so this is where the hours are set. Null for a visitor. */
  mySchedule?: ScheduleDto | null;
  /** The page clock, for saying when the next call is. */
  serverNow: number;
  /** The card that carries the plan the headline names — the first call, or
      the first pass — laid under the sentence instead of beside it. */
  children?: ReactNode;
  /** On the mate page: the referral edge the calendar's checkout anchors first. */
  squadAlias?: string | null;
  /** Why the sale the viewer was in the middle of is gone, under the headline. */
  note?: string | null;
}

export function IntroCard({
  tutorUsername,
  tutorName,
  loggedIn,
  playbackId,
  picture,
  bundle,
  nextCallAt,
  hasPass = false,
  timezone,
  offerOnly = false,
  isOwner = false,
  mySchedule = null,
  serverNow,
  children,
  squadAlias,
  note,
}: IntroCardProps) {
  const now = useClock(serverNow);
  // The masthead above already carries the full name; a column repeating it
  // reads as talk about a stranger.
  const name = firstName({ name: tutorName, username: tutorUsername });

  // One ladder, first true wins: the owner reads their week or the trial's absence;
  // a viewer's own plans outrank a sale; and with no sale the grid is the ask.
  const headline = isOwner
    ? bundle
      ? ownerHoursLine(timezone)
      : 'No intro call on this page yet'
    : nextCallAt
      ? timezone
        ? nextCallLine(name, callWhen(nextCallAt, now, timezone), timezone)
        : `Your next call with ${name}`
      : hasPass
        ? `Pick a time to book your call with ${name}`
        : bundle
          ? pickTimeAsk(name, timezone)
          : offerOnly
            ? null
            : loggedIn
              ? `Buy a pack to book calls with ${name}`
              : 'Sign in to see your upcoming calls';

  return (
    <div className="overflow-hidden rounded-[var(--sf-radius)] bg-[var(--sf-card)]">
      <div className="px-3.5 pt-3.5">
        {isOwner ? (
          // The owner's slot is the one that is not a preview: their video with
          // a Replace control over it, or the invitation to add one.
          <OwnerIntroVideo
            name={tutorName}
            username={tutorUsername}
            playbackId={playbackId ?? null}
            picture={picture ?? null}
          />
        ) : (
          <IntroVideo name={name} playbackId={playbackId ?? null} picture={picture} priority />
        )}
      </div>

      <div className="flex flex-col gap-2 px-3.5 pb-3.5 pt-3">
        {headline && (
          <span
            className="sf-title text-title-2 font-extrabold text-[var(--sf-ink)]"
            suppressHydrationWarning
          >
            {headline}
          </span>
        )}
        {note && <p className="text-p-3 text-[var(--sf-mut)]">{note}</p>}
        {isOwner ? (
          // Nobody books their own call, so the owner edits the week behind the
          // calendar instead of reading it. The price is on the grid's card.
          <OwnerHours schedule={mySchedule} username={tutorUsername} />
        ) : children ? (
          children
        ) : bundle ? (
          // The question above, the answer open under it: pick a time, then pay
          // for it. A signed-out visitor gets the same calendar — the sign-in
          // stands one step in, in the card that asks for the money, with the
          // hour already chosen. A form at the door asked before it offered.
          <BookSlotCard
            bundle={bundle}
            tutorUsername={tutorUsername}
            loggedIn={loggedIn}
            isOwner={isOwner}
            firstName={name}
            authSource="calls_block"
            surface="card"
            serverNow={serverNow}
            squadAlias={squadAlias}
          />
        ) : !loggedIn && !offerOnly ? (
          // Nothing to sell and nobody signed in: the door is the one thing
          // left to offer, and a line that says "sign in" without one is inert.
          <InlineAuth
            id="intro"
            source="calls_block"
            tutorUsername={tutorUsername}
            sourceSquad={squadAlias}
            tone="card"
            cta="Sign in"
          />
        ) : null}
      </div>
    </div>
  );
}
